import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  StaffLeaveDecision,
  StaffLeaveRequest,
  StaffLeaveSession,
} from '@/types/staffLeave';
import type { StaffAttendanceMonth } from '@/types/staffAttendance';
import type { StaffLeavePayload } from '../schemas/leave.schema';

/**
 * One member of staff's attendance and leave, as the office reads them.
 *
 * Both are about *somebody else's* record, which is why the staff uuid is in
 * the path here while the teacher's own equivalents take none. The institute is
 * still the caller's own, from the token, so a uuid from another institute is a
 * 404 rather than a way across.
 *
 * The academic year is never sent: the entitlement belongs to the institute's
 * current session, which the server resolves.
 */
export const staffRecordApi = {
  /** `month` is `YYYY-MM`; omitted, the server answers for the current one. */
  attendanceMonth: (uuid: string, month?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<StaffAttendanceMonth>>(`/institute/staff/${uuid}/attendance`, {
        params: month ? { month } : undefined,
      }),
    ),

  /** The current session's, or `academicYearUuid`'s when a past one is chosen. */
  leave: (uuid: string, academicYearUuid?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<StaffLeaveSession>>(`/institute/staff/${uuid}/leave`, {
        params: academicYearUuid ? { academicYearUuid } : undefined,
      }),
    ),

  apply: (uuid: string, payload: StaffLeavePayload) =>
    unwrap(apiClient.post<ApiSuccess<StaffLeaveRequest>>(`/institute/staff/${uuid}/leave`, payload)),

  decide: (leaveUuid: string, status: StaffLeaveDecision, decisionNote?: string) =>
    unwrap(
      apiClient.patch<ApiSuccess<StaffLeaveRequest>>(
        `/institute/staff-leave/${leaveUuid}/decision`,
        { status, ...(decisionNote ? { decisionNote } : {}) },
      ),
    ),

  /** Withdraws it. `CANCELLED`, not a delete — the log records what was asked. */
  cancel: (leaveUuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<StaffLeaveRequest>>(`/institute/staff-leave/${leaveUuid}`)),
};
